
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import { NestApiType } from '@/composables/nestApi';
import { DataExplorerDataType } from '@/types';
import { currencies } from '@/configs/currencies';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
  SpmActiveSort,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';
import { formatAmount } from '@/components/table-rest-api/formatters';

export default defineComponent({
  name: 'OrdersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    DataExplorerLink,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const userLocale = ref<string>();

    // Currency list for filters
    const currencyList = currencies.map((currency: {code: string; label: string}) => ({
      value: currency.code,
      label: currency.label,
    }));

    const initialSorting: SpmActiveSort = { field: 'updated_at', order: 'desc' };

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/orders',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'order_id',
        header: '',
        fields: [{
          name: 'order_id',
          label: t('myShopData.orders.headers.id_order'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'order_info',
        header: t('myShopData.orders.headers.order_reference'),
        fields: [{
          name: 'reference',
          label: t('myShopData.orders.headers.order_reference'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'date',
          label: t('myShopData.orders.headers.date'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.orders.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'created_at',
        header: t('myShopData.orders.headers.date_creation'),
        fields: [{
          name: 'created_at',
          label: t('myShopData.orders.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'customer_info',
        header: t('myShopData.orders.headers.customer'),
        fields: [{
          name: 'customer_first_name',
          label: t('myShopData.orders.headers.first_name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'customer_last_name',
          label: t('myShopData.orders.headers.last_name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'customer_email',
          label: t('myShopData.orders.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'customer_deleted',
          label: t('myShopData.orders.headers.deleted'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { value: '0', label: t('myShopData.orders.headers.deleted') },
              { value: '1', label: t('myShopData.orders.headers.notDeleted') },
            ],
          },
        }],
      },
      {
        id: 'amounts',
        header: t('myShopData.orders.headers.amounts'),
        fields: [{
          name: 'amount',
          label: t('myShopData.orders.headers.amount'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'amount_without_tax',
          label: t('myShopData.orders.headers.amount_without_tax'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'numberInput',
          },
        }, {
          name: 'currency',
          label: t('myShopData.orders.headers.currency'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: currencyList,
          },
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.orders.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.orders.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'is_confirmed',
        header: t('myShopData.orders.headers.is_valid'),
        fields: [{
          name: 'is_confirmed',
          label: t('myShopData.orders.headers.is_valid'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { value: '1', label: t('active') },
              { value: '0', label: t('inactive') },
            ],
          },
        }],
      },
    ];

    // Helper function to clean strings
    const cleanStr = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(value).trim();
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Wrapper to use formatAmount with user locale
    const formatAmountWithLocale = (amount: number | string | null | undefined, currency: string): string => formatAmount(amount, currency, userLocale.value || 'fr-FR');

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/orders',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.orders.export.success'));
        // } else {
        //   await showToastError(t('myShopData.orders.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.orders.export.error'));
      }
    };

    return {
      t,
      apiCall,
      columns,
      persistentFilters,
      cleanStr,
      formatFullName,
      formatAmount: formatAmountWithLocale,
      handleExport,
      initialSorting,
      DataExplorerDataType,
    };
  },
});
